import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconMessages } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { extractApiErrorMessage } from '@frontend/api/http';

import {
  createMessageTrigger,
  deleteMessageTrigger,
  getMessageTrigger,
  updateMessageTrigger,
} from '../../services/message-trigger';
import { getTriggerSelfUrl } from '../../types/api';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Message Trigger node type.
 * Triggers workflow execution from incoming messages.
 */
export const messageTriggerConfig = defineNode({
  description: {
    displayName: 'Message Trigger',
    name: NODE_TYPE.MESSAGE,
    description: 'Trigger workflow execution from incoming messages',
    iconInfo: {
      icon: IconMessages,
      color: 'fg.success',
    },
    category: NodeTypeCategory.TRIGGER,
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this message trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of what messages trigger this workflow',
      },
    ],
    defaults: {
      name: 'Message Trigger',
      parameters: {
        name: '',
        description: '',
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const selfUrl = getTriggerSelfUrl(nodeData);
    return Boolean(selfUrl && selfUrl.trim().length > 0);
  },

  onAsyncSave: async (
    _nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      try {
        const messageTrigger = await createMessageTrigger(
          context.workflowAgentId,
          context.instanceId,
          data
        );
        return messageTrigger;
      } catch (error) {
        const errorMessage = extractApiErrorMessage(error);
        throw new Error(errorMessage);
      }
    }

    try {
      await updateMessageTrigger(selfUrl, data);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      const messageTrigger = await getMessageTrigger(selfUrl);
      return messageTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch message trigger:', error);
      toaster.create('Failed to load message trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    const selfUrl = getTriggerSelfUrl(context.nodeData);
    if (!selfUrl) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteMessageTrigger(selfUrl);
    } catch (error) {
      console.error('Failed to delete message trigger:', error);
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  render: memo(function MessageTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
